import { cn } from "@gotts.ai/ui";
import { type ReactNode, useState } from "react";

interface CollapsibleSectionProps {
  title: string | ReactNode;
  defaultOpen?: boolean;
  badge?: string;
  children: ReactNode;
  className?: string;
  borderColor?: string;
  previewText?: string;
  open?: boolean;
  onToggle?: (open: boolean) => void;
}

export function CollapsibleSection({
  title,
  defaultOpen = true,
  badge,
  children,
  className,
  borderColor,
  previewText,
  open: controlledOpen,
  onToggle,
}: CollapsibleSectionProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  function handleToggle() {
    const next = !isOpen;
    if (isControlled) {
      onToggle?.(next);
    } else {
      setInternalOpen(next);
      onToggle?.(next);
    }
  }

  return (
    <div
      className={cn("border-b border-[var(--glass-border)]", className)}
      style={
        borderColor
          ? {
              borderLeftWidth: 3,
              borderLeftColor: borderColor,
              borderLeftStyle: "solid",
            }
          : undefined
      }
    >
      <button
        type="button"
        onClick={handleToggle}
        className="flex w-full items-center gap-2 py-2 text-left"
      >
        <span
          className={cn(
            "text-text-muted inline-block text-xs transition-transform duration-[450ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
            isOpen && "rotate-90",
          )}
        >
          {"\u25B6"}
        </span>
        <span className="font-serif text-[15px] font-light text-[var(--color-text)]">
          {title}
        </span>
        {badge && (
          <span className="rounded-full bg-[rgba(255,255,255,0.08)] px-2 py-0.5 font-mono text-[10px] text-[var(--color-text-muted)]">
            {badge}
          </span>
        )}
        {!isOpen && previewText && (
          <span className="ml-1 truncate font-sans text-[11px] italic text-[var(--color-text-muted)]">
            {previewText}
          </span>
        )}
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-[450ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
          isOpen ? "max-h-[5000px] pb-3 opacity-100" : "max-h-0 opacity-0",
        )}
      >
        {children}
      </div>
    </div>
  );
}
